import { Auth, AuthGuard } from '@consigli/oauth';
import { getSolutionViewerUrl } from '@consigli/utils';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
} from 'react-router';

import { AuthCheckingScreen } from '@/layouts/auth-checking-screen';
import { UserGuard } from '@/layouts/user-guard';
import { AgentsDirectoryRoute } from '@/pages/agents-directory/agents-directory-route';
import { ProjectCreationRoute } from '@/pages/project-creation/project-creation-route';
import { ProjectListRoute } from '@/pages/project-list/project-list-route';
import { ProjectOverviewRoute } from '@/pages/project-overview/project-overview-route';
import { RootPage } from '@/pages/root';
import { ServiceListRoute } from '@/pages/service-list/service-list-route';
import { authConfig } from '@/util/oauth/config';

import { InviteUser } from './features/invite-user/invite-user';
import { ReportsPage } from './features/package-creation/site-due-diligence/reports-page';
import { WizardWrapper } from './features/package-creation/wizard-wrapper';
import { PageNotFound } from './layouts/page-not-found';
import { ActionItemsOverviewPage } from './pages/meeting-secretary/action-items-overview-page';
import { MeetingSecretaryCalendar } from './pages/meeting-secretary/meeting-secretary-calendar';
import { MeetingSecretaryDashboard } from './pages/meeting-secretary/meeting-secretary-dashboard';
import { MeetingSecretaryDetailWrapper } from './pages/meeting-secretary/meeting-secretary-detail-wrapper';
import { MeetingSecretaryMeetings } from './pages/meeting-secretary/meeting-secretary-meetings';
import { MeetingSecretarySettings } from './pages/meeting-secretary/meeting-secretary-settings';
import { MeetingSecretaryTemplates } from './pages/meeting-secretary/meeting-secretary-templates';
import { ProjectListWrapper } from './pages/project-list/project-list-wrapper';
import { RouteErrorPage } from './pages/route-error-page';
import { ServiceDetailWrapper } from './pages/service-detail-wrapper';
import { ServiceListWrapper } from './pages/service-list/service-list-wrapper';
import { UserAndProjectManagementPage } from './pages/user-and-project-management/user-and-project-management-page';
import { UserManagementWrapper } from './pages/user-and-project-management/user-management/user-management-wrapper';
import { UserProfileRoute } from './pages/user-profile/user-profile-route';
import { Route as NavRoute } from './routes';
import { RouteRenderer } from './service-routes';

export const routes = createRoutesFromElements(
  /**
   * The pathless parent exists only to carry `errorElement` for every top-level
   * route. React Router never propagates route errors to the boundary in
   * `app.tsx`, so with this on `/` alone its siblings fell through to React
   * Router's own bare error page and reported nothing.
   *
   * Only synchronous render throws reach it — a `useMyUser()` or a bad
   * destructure. Async failures surface as unhandled rejections instead.
   */
  <Route errorElement={<RouteErrorPage />}>
    <Route
      path="/"
      element={
        <AuthGuard authConfig={authConfig} loading={<AuthCheckingScreen />}>
          <UserGuard>
            <RootPage />
          </UserGuard>
        </AuthGuard>
      }
    >
      <Route index element={<Navigate to={`/${NavRoute.PROJECTS}`} />} />
      <Route path={`/${NavRoute.NEW_PROJECT}`} element={<ProjectCreationRoute />} />
      <Route path={NavRoute.PROJECTS} element={<ProjectListWrapper />}>
        <Route index element={<ProjectListRoute />} />

        {/* Three flat siblings, one component, each gated on the redesign flag.
            SETTINGS is already in the enum and already routed below under
            :serviceName — same string, different path, no conflict. */}
        <Route
          path={`:projectId/${NavRoute.AGENTS}`}
          element={<ProjectOverviewRoute tab={NavRoute.AGENTS} />}
        />
        <Route
          path={`:projectId/${NavRoute.USERS}`}
          element={<ProjectOverviewRoute tab={NavRoute.USERS} />}
        />
        <Route
          path={`:projectId/${NavRoute.SETTINGS}`}
          element={<ProjectOverviewRoute tab={NavRoute.SETTINGS} />}
        />

        {/* Path untouched, so every notification email keeps working: only the
            index element becomes a flag gate, and the :serviceName subtree is
            not edited at all. */}
        <Route path={`:projectId/${NavRoute.SERVICES}`} element={<ServiceListWrapper />}>
          <Route index element={<ServiceListRoute />} />
          <Route path=":serviceName" element={<ServiceDetailWrapper />}>
            <Route index element={<WizardWrapper />} />
            <Route path={NavRoute.CREATE} element={<WizardWrapper />} />
            <Route path={NavRoute.REPORTS} element={<ReportsPage />} />
            <Route path={NavRoute.PACKAGES} element={<Outlet />}>
              <Route path="*" element={<RouteRenderer />} />
            </Route>
            <Route path="*" element={<MeetingSecretaryDetailWrapper />}>
              <Route path={NavRoute.DASHBOARD} element={<MeetingSecretaryDashboard />} />
              <Route path={NavRoute.ACTION_ITEMS} element={<ActionItemsOverviewPage />} />
              <Route path={NavRoute.MEETINGS} element={<MeetingSecretaryMeetings />} />
              <Route path={NavRoute.CALENDAR} element={<MeetingSecretaryCalendar />} />
              <Route path={NavRoute.TEMPLATES} element={<MeetingSecretaryTemplates />} />
              <Route path={NavRoute.SETTINGS} element={<MeetingSecretarySettings />} />
            </Route>
          </Route>
        </Route>
      </Route>
      <Route path={NavRoute.USER_MANAGEMENT} element={<UserManagementWrapper />}>
        <Route index element={<UserAndProjectManagementPage />} />
        <Route path={NavRoute.INVITE_USER} element={<InviteUser />}></Route>
      </Route>
      {/* Global, and a sibling of `profile` rather than anything under
          `projects`: the directory is the catalogue, not a project's applied
          services. The nested `:projectId/agents` above is a different URL. */}
      <Route path={NavRoute.AGENTS} element={<AgentsDirectoryRoute />} />
      <Route path="profile" element={<UserProfileRoute />}></Route>
    </Route>
    <Route
      path={`/${NavRoute.SOLUTION_VIEWER}`}
      element={<Navigate to={getSolutionViewerUrl()} replace />}
    />
    <Route path="/logout" element={<Navigate to={`/${NavRoute.PROJECTS}`} />} />
    <Route
      path="/auth"
      element={
        <Auth
          destination={sessionStorage.getItem('savedPath') || `/${NavRoute.ROOT}`}
          authConfig={authConfig}
        />
      }
    />
    <Route path="*" element={<PageNotFound />} />
  </Route>,
);

export const router = createBrowserRouter(routes);
